/*
 * Synced reader preferences (issue 291, split out of 199): the account-side
 * tier of the preferences the local tier persists per browser — the
 * localStorage 'typography' bag, plus the 'theme' and 'readerMode' keys that
 * deliberately keep their own storage (decision on #199; the server unifies
 * all three in ONE jsonb bag, the client keys do not move).
 *
 * THE PRE-PAINT BOOTSTRAP ONLY EVER READS LOCALSTORAGE. theme-bootstrap.js
 * runs before React and before any network call, so the synced tier works by
 * WRITING THROUGH localStorage: applying a server bag here updates the same
 * keys the bootstrap reads, and the next load pre-paints the synced values
 * with no flash and no new code in the bootstrap. This module never renders
 * anything; it moves values between the server row and the local keys and
 * lets the existing appliers repaint.
 *
 * ── THE CONFLICT RULE (the issue asked for this to be decided, not left) ──
 *
 * A reader can hold two preference sets: this browser's and the account's.
 *
 * 1. ON SIGN-IN (an explicit login/register in this browser): MERGE BY KEY,
 *    and a key the reader EXPLICITLY set in this browser wins over the
 *    account's value; keys never set here adopt the account's. The merged
 *    result is pushed back up so every device converges on it.
 *      - Why not server-wins: the local explicit value is what the reader is
 *        looking at RIGHT NOW — often chosen minutes ago on a new laptop —
 *        and repainting the page at sign-in with a two-year-old choice from
 *        their phone is the failure mode the issue names.
 *      - Why not local-wins wholesale: a fresh browser has no explicit local
 *        keys, so under merge-by-key the account arrives untouched — the
 *        other expectation ("my settings follow me") — without a rule of its
 *        own. Explicitness is legible from storage: each local key exists
 *        only once the reader has chosen (defaults remove the key).
 *
 * 2. ON A SIGNED-IN LOAD (session restored from tokens): SERVER WINS. Every
 *    signed-in change is pushed at write time, so the server row is the LAST
 *    write from ANY of the reader's devices — applying it is last-write-wins
 *    across devices without trusting device clocks. A change made on the
 *    phone last night arrives on the laptop this morning.
 *      - Exception: an account with NO row yet (updated_at null — the account
 *        predates the feature, or the first push never landed) must not blank
 *        this device; instead the local state SEEDS the account.
 *
 * 3. ON SIGN-OUT: the local keys are left alone. The device keeps the look it
 *    has — sign-out changes who you are, not what you can read comfortably —
 *    and the signed-out tier keeps working exactly as it shipped in #199.
 *
 * Known edges, accepted deliberately: reader mode's OFF state removes its key
 * (its storage shape), so "explicitly off" is indistinguishable from "never
 * set" and the account's ON can win at sign-in — Escape is one key. And a
 * server-applied change repaints mid-session on the receiving device; that is
 * inherent to sync and only happens when another device really did change
 * something.
 *
 * Axios is required LAZILY, only on a real network call: this module is
 * imported by Layout, the typography menu and the reader-mode button, and a
 * top-level axios import would drag untransformed axios ESM into every jsdom
 * test that renders them (the reason AuthContext's test mocks it).
 */

import { getAccessToken, getRefreshToken } from './authStore';
import {
  DEFAULT_TYPOGRAPHY,
  TEXT_SIZES,
  DENSITIES,
  FONTS,
  applyTypography,
} from './typography';
import { THEMES, applyTheme } from './theme';

/** Fired on window after a server bag has been applied to this browser, so
 *  mounted UI that mirrors a preference in React state (Layout's theme
 *  toggle) can resync from the document. */
export const PREFERENCES_APPLIED_EVENT = 'tdt:preferences-applied';

const PUSH_DEBOUNCE_MS = 800;

const signedIn = () => !!(getAccessToken() || getRefreshToken());

// Lazy for the module-header reason.
function api() {
  // eslint-disable-next-line global-require
  return require('./axios').default;
}

const readKey = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeKey = (key, value) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch (e) {
    /* private mode etc. — the in-page apply still happened */
  }
};

/** A typography object validated field by field against the closed
 *  vocabularies, or null when the input is not an object at all. */
function cleanTypography(t) {
  if (!t || typeof t !== 'object' || Array.isArray(t)) return null;
  return {
    size: TEXT_SIZES.includes(t.size) ? t.size : DEFAULT_TYPOGRAPHY.size,
    density: DENSITIES.includes(t.density) ? t.density : DEFAULT_TYPOGRAPHY.density,
    font: FONTS.includes(t.font) ? t.font : DEFAULT_TYPOGRAPHY.font,
  };
}

const isDefaultTypography = (t) =>
  t.size === DEFAULT_TYPOGRAPHY.size &&
  t.density === DEFAULT_TYPOGRAPHY.density &&
  t.font === DEFAULT_TYPOGRAPHY.font;

// ── Allowed sources (issue #310) ─────────────────────────────────────────────
// The reader's saved set of source books, keyed BY SYSTEM SLUG (a rulebook
// slug only means anything inside one game system; the bag itself stays
// system-agnostic — the #291 decision — the key carries the axis). It rides
// this same bag because Tom's standing rule on #291 forbids a second
// preferences store: `tdt.user_preferences` is the one, and the SERVER reads
// this key per request to scope lists, search and the generators. Unlike the
// other keys the vocabulary is OPEN (rulebook slugs), so validation is shape
// and bounds, mirroring server/utils/allowedSources.js: junk degrades to
// "not set", never to a different set.
const MAX_SOURCE_SYSTEMS = 20;
const MAX_SOURCES_PER_SYSTEM = 200;
const MAX_SOURCE_SLUG_LENGTH = 200;

/** One system's list validated: a deduped array of slug strings, or null. */
export function cleanSourceList(raw) {
  if (!Array.isArray(raw)) return null;
  const out = [];
  const seen = new Set();
  for (const value of raw) {
    if (typeof value !== 'string') return null;
    const slug = value.trim();
    if (!slug || slug.length > MAX_SOURCE_SLUG_LENGTH || slug.includes(' ')) return null;
    if (seen.has(slug)) continue;
    seen.add(slug);
    out.push(slug);
    if (out.length > MAX_SOURCES_PER_SYSTEM) return null;
  }
  return out.length ? out : null;
}

/** The whole allowedSources map validated system by system, or null. */
export function cleanAllowedSourcesMap(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out = {};
  let systems = 0;
  for (const [key, value] of Object.entries(raw)) {
    if (typeof key !== 'string' || !key.trim() || key.length > 100) continue;
    const list = cleanSourceList(value);
    if (!list) continue;
    out[key] = list;
    systems += 1;
    if (systems >= MAX_SOURCE_SYSTEMS) break;
  }
  return Object.keys(out).length ? out : null;
}

// Hover-card fields (issue 376): `hoverFields[<system>][<family key>]` is an
// ORDERED list of attribute keys — order is part of the choice — capped at
// the card's own limit. Validated like allowedSources: junk degrades to "not
// set", and a key the family no longer declares is dropped at render, never
// here (this module knows no registry).
const MAX_HOVER_SYSTEMS = 20;
const MAX_HOVER_FAMILIES = 60;
export const MAX_HOVER_FIELDS = 8;
const MAX_HOVER_KEY_LENGTH = 64;
const HOVER_KEY_RE = /^[a-z0-9][a-z0-9_-]*$/; // an attribute key, as the tree spells them

/** One family's list validated: a deduped array of attribute keys, or null. */
export function cleanHoverFieldList(raw) {
  if (!Array.isArray(raw)) return null;
  const out = [];
  const seen = new Set();
  for (const value of raw) {
    if (typeof value !== 'string') return null;
    const key = value.trim();
    if (!key || key.length > MAX_HOVER_KEY_LENGTH || !HOVER_KEY_RE.test(key)) return null;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(key);
    if (out.length > MAX_HOVER_FIELDS) return null;
  }
  return out.length ? out : null;
}

/** The whole hoverFields map validated system by system, family by family, or null. */
export function cleanHoverFieldsMap(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out = {};
  let systems = 0;
  for (const [system, families] of Object.entries(raw)) {
    if (typeof system !== 'string' || !system.trim() || system.length > 100) continue;
    if (!families || typeof families !== 'object' || Array.isArray(families)) continue;
    const cleanFamilies = {};
    let n = 0;
    for (const [family, list] of Object.entries(families)) {
      if (typeof family !== 'string' || !family.trim() || family.length > 100) continue;
      const clean = cleanHoverFieldList(list);
      if (!clean) continue;
      cleanFamilies[family] = clean;
      n += 1;
      if (n >= MAX_HOVER_FAMILIES) break;
    }
    if (!Object.keys(cleanFamilies).length) continue;
    out[system] = cleanFamilies;
    systems += 1;
    if (systems >= MAX_HOVER_SYSTEMS) break;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * This browser's EXPLICIT preferences, as the server bag shape. A key appears
 * only when the reader has actually chosen something here — every local store
 * removes its key at the default — which is exactly the "explicitness" the
 * sign-in merge trades on.
 */
export function collectLocal() {
  const bag = {};
  const rawTypography = readKey('typography');
  if (rawTypography) {
    try {
      const clean = cleanTypography(JSON.parse(rawTypography));
      if (clean) bag.typography = clean;
    } catch (e) {
      /* corrupted bag: not an explicit choice */
    }
  }
  const theme = readKey('theme');
  // The full named set (#314) — the closed vocabulary lives in config/theme.js.
  if (THEMES.includes(theme)) bag.theme = theme;
  if (readKey('readerMode') === '1') bag.readerMode = true;
  const rawSources = readKey('allowedSources');
  if (rawSources) {
    try {
      const clean = cleanAllowedSourcesMap(JSON.parse(rawSources));
      if (clean) bag.allowedSources = clean;
    } catch (e) {
      /* corrupted map: not an explicit choice */
    }
  }
  const rawHover = readKey('hoverFields');
  if (rawHover) {
    try {
      const clean = cleanHoverFieldsMap(JSON.parse(rawHover));
      if (clean) bag.hoverFields = clean;
    } catch (e) {
      /* corrupted map: not an explicit choice */
    }
  }
  return bag;
}

/**
 * Validate a server bag against the client vocabularies — the stored row is
 * untrusted input on the way back in (the saved_search rule): the server
 * deliberately does not interpret it, so an unknown key, a retired value or
 * junk written by an old client degrades to "not set" here rather than
 * poisoning the local keys.
 */
export function cleanServerBag(raw) {
  const bag = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return bag;
  const typography = cleanTypography(raw.typography);
  if (typography && !isDefaultTypography(typography)) bag.typography = typography;
  if (THEMES.includes(raw.theme)) bag.theme = raw.theme;
  if (raw.readerMode === true) bag.readerMode = true;
  const allowedSources = cleanAllowedSourcesMap(raw.allowedSources);
  if (allowedSources) bag.allowedSources = allowedSources;
  const hoverFields = cleanHoverFieldsMap(raw.hoverFields);
  if (hoverFields) bag.hoverFields = hoverFields;
  return bag;
}

/**
 * Write a (cleaned) bag through the local keys and repaint. A key ABSENT from
 * the bag clears its local key back to the default — the bag is always the
 * full picture (PUT is full-replace serverside for the same reason).
 *
 * readerMode only writes storage: the reader-mode CLASS is owned by whichever
 * detail page is mounted (useReaderMode), and the preference reaching storage
 * is what its next mount reads — same division of labour as the bootstrap.
 */
export function applyBag(bag) {
  // Typography: reuse the module's own applier so a synced apply and a menu
  // change write byte-identical attributes.
  const typography = bag.typography || { ...DEFAULT_TYPOGRAPHY };
  writeKey('typography', bag.typography ? JSON.stringify(bag.typography) : null);
  applyTypography(typography);

  // Theme: one applier for the named set (#314) — the same class + data-theme
  // writes config/theme.js and the pre-paint bootstrap perform. Absent = the
  // shipped default (dark, key removed — the pre-#199 no-preference state the
  // bootstrap defaults to).
  writeKey('theme', bag.theme || null);
  applyTheme(bag.theme || 'dark');

  writeKey('readerMode', bag.readerMode === true ? '1' : null);

  // Allowed sources (#310): storage only — nothing repaints; the surfaces
  // that consult it (AllowedSourcesNotice, the Account card) resync off the
  // event below.
  writeKey('allowedSources',
    bag.allowedSources ? JSON.stringify(bag.allowedSources) : null);
  // Hover-card fields (#376): storage only, read at hover time by the card.
  writeKey('hoverFields', bag.hoverFields ? JSON.stringify(bag.hoverFields) : null);

  try {
    window.dispatchEvent(new Event(PREFERENCES_APPLIED_EVENT));
  } catch (e) {
    /* non-browser environment */
  }
}

const canonical = (bag) =>
  JSON.stringify({
    typography: bag.typography || null,
    theme: bag.theme || null,
    readerMode: bag.readerMode === true,
    allowedSources: bag.allowedSources || null,
    hoverFields: bag.hoverFields || null,
  });

async function fetchServerBag() {
  const res = await api().get('/api/preferences');
  return {
    hasRow: !!(res && res.data && res.data.updated_at),
    bag: cleanServerBag(res && res.data ? res.data.prefs : null),
  };
}

async function putBag(bag) {
  await api().put('/api/preferences', { prefs: bag });
}

/**
 * Rule 1 — explicit sign-in in this browser. Merge by key (local explicit
 * wins), apply the merged picture, and push it up if it differs from what the
 * account held, so the reader's devices converge on what they are looking at.
 * Best-effort throughout: a network failure leaves the local tier standing.
 */
export async function syncOnSignIn() {
  if (!signedIn()) return;
  try {
    const { hasRow, bag: server } = await fetchServerBag();
    const local = collectLocal();
    const merged = { ...server, ...local };
    // allowedSources merges ONE LEVEL DOWN (#310): the map is keyed by system
    // slug and each system's set is an independent choice, so a set saved
    // here for pf-1e must not wipe the account's dnd-3.5 set the way a
    // wholesale key replacement would. Per system, local explicit still wins.
    if (server.allowedSources && local.allowedSources) {
      merged.allowedSources = { ...server.allowedSources, ...local.allowedSources };
    }
    applyBag(merged);
    // Push only when there is something to say: an all-defaults sign-in must
    // NOT mint an empty row, because a row's existence is what lets rule 2
    // treat the account as having spoken — an empty row minted here would
    // blank another device's not-yet-pushed preferences on its next restore.
    if (hasRow ? canonical(merged) !== canonical(server) : Object.keys(merged).length > 0) {
      await putBag(merged);
    }
  } catch (e) {
    /* sync is best-effort; the local tier stands */
  }
}

/**
 * Rule 2 — a load that restored an existing session. The account's row is the
 * last write from any device, so it wins — unless the account has no row yet,
 * in which case this device's state seeds it rather than being blanked by it.
 */
// `preloaded` is the bag as GET /api/auth/me now carries it (`{ prefs,
// updated_at }`), so a restored session applies the account's preferences
// without a second request; without it the bag is fetched as before.
export async function syncOnRestore(preloaded) {
  if (!signedIn()) return;
  try {
    const { hasRow, bag: server } = preloaded && typeof preloaded === 'object'
      ? { hasRow: !!preloaded.updated_at, bag: cleanServerBag(preloaded.prefs) }
      : await fetchServerBag();
    if (!hasRow) {
      const local = collectLocal();
      if (Object.keys(local).length) await putBag(local);
      return;
    }
    applyBag(server);
  } catch (e) {
    /* sync is best-effort; the local tier stands */
  }
}

let pushTimer = null;

// Pushes go up ONE AT A TIME, in order (2026-09-26). Each carries the full
// snapshot and the server keeps whichever arrives last, so two pushes in
// flight at once could land out of order and leave the account holding the
// OLDER state — which the next signed-in load then restores over the newer
// one. The production e2e run lost a hover-field choice exactly that way:
// three quick edits, three concurrent PUTs, the first one landing last. Each
// push waits for the one before it and collects the snapshot when it is sent,
// so the last to land is always the newest.
let pushChain = Promise.resolve();
function queuePush() {
  const run = pushChain.then(() => (signedIn()
    ? putBag(collectLocal()).then(() => true).catch(() => false)
    : false));
  pushChain = run;
  return run;
}

/**
 * Called by the three preference writers (typography menu, theme toggle,
 * reader-mode button) after any local change. Signed out it is a no-op — the
 * shipped local-only behaviour, untouched. Signed in it debounces a PUT of
 * the full local snapshot, which is what makes "server wins on restore"
 * last-write-wins across devices. Fire-and-forget: a failed push costs
 * nothing locally and the next change or sign-in re-converges.
 */
export function schedulePush() {
  if (!signedIn()) return;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    if (!signedIn()) return;
    queuePush(); // best-effort: a failure resolves false and nothing waits on it
  }, PUSH_DEBOUNCE_MS);
}

/**
 * Push the full local snapshot NOW, not after the debounce (#310): the server
 * reads `allowedSources` per request, so the Account card awaits this before
 * telling the reader the set is saved — otherwise the next list they open
 * races an 800ms timer. Signed out it is a no-op like schedulePush; failures
 * resolve (best-effort, the local tier stands) and the caller may inspect the
 * boolean.
 */
export function pushNow() {
  if (!signedIn()) return Promise.resolve(false);
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
  return queuePush();
}

/** Test seam: flush a pending debounced push immediately. */
export function _cancelScheduledPush() {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = null;
}
